import React, { useEffect, useState } from "react";
import { Loader2, FileInput } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../../components/ui/table";
import { useCentralStore } from "../../../../store/Central";
import { PriceListImport } from "../../../../types/price-list.types";

interface PriceListImportDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const PriceListImportDialog: React.FC<PriceListImportDialogProps> = ({
	open,
	onOpenChange,
}) => {
	const {
		priceListImports,
		priceListImportsLoading,
		loadPriceListImports,
		importPriceListItems,
	} = useCentralStore((state) => ({
		priceListImports: state.priceListImports,
		priceListImportsLoading: state.priceListImportsLoading,
		loadPriceListImports: state.loadPriceListImports,
		importPriceListItems: state.importPriceListItems,
	}));

	const [importingId, setImportingId] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			loadPriceListImports();
		}
	}, [open, loadPriceListImports]);

	const handleImport = async (importItem: PriceListImport) => {
		setImportingId(importItem.id);
		
		const result = await importPriceListItems(importItem.id);
		
		if (result) {
			// Close dialog after successful import
			onOpenChange(false);
		}
		
		setImportingId(null);
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleDateString("de-DE");
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Preisliste importieren</DialogTitle>
					<DialogDescription>
						Wählen Sie eine Preislisten-Vorlage zum Importieren aus.
					</DialogDescription>
				</DialogHeader>

				{priceListImportsLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-gray-500" />
					</div>
				) : priceListImports.length === 0 ? (
					<div className="text-center py-12 text-gray-500">
						Keine Import-Vorlagen verfügbar. Legen Sie erst eine Region fest (vorheriger Schritt).
					</div>
				) : (
					<div className="border rounded-lg">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Beschreibung</TableHead>
									<TableHead className="w-32">Startdatum</TableHead>
									<TableHead className="w-32">Enddatum</TableHead>
									<TableHead className="w-32"></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{priceListImports.map((importItem) => (
									<TableRow key={importItem.id}>
										<TableCell className="font-medium">
											{importItem.name}
										</TableCell>
										<TableCell>
											{importItem.description || "-"}
										</TableCell>
										<TableCell>
											{formatDate(importItem.start_date)}
										</TableCell>
										<TableCell>
											{formatDate(importItem.end_date)}
										</TableCell>
										<TableCell>
											<Button
												onClick={() => handleImport(importItem)}
												disabled={importingId === importItem.id}
												size="sm"
											>
												{importingId === importItem.id ? (
													<>
														<Loader2 className="h-4 w-4 animate-spin mr-2" />
														Importiere...
													</>
												) : (
													<>
														<FileInput className="h-4 w-4 mr-2" />
														Importieren
													</>
												)}
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};